// Domains.jsx
import React, { useState } from "react";
import { Eye, Trash2, RefreshCw, Plus } from "lucide-react";

const Domain = ({
  domain,
  subdomains,
  resultInfo,
  summary,
  loading,
  creating,
  busyById,
  newName,
  newContent,
  isIpLike,     // already used to gate the Create button
  ipOptions = [], // ✅ NEW: list of allowed IPs from parent
  setDomain,
  setNewName,
  setNewContent,
  onFetch,
  onCreate,
  onDelete,
}) => {
  const [selectedRow, setSelectedRow] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const rows = Array.isArray(subdomains) ? subdomains : [];

  return (
    <div className="border rounded p-3">
      {/* Domain + Fetch */}
      <div className="row g-2 align-items-end">
        <div className="col-md-5">
          <label className="admin-field-label">Domain</label>
          <input
            className="form-control form-control-sm"
            placeholder="e.g., chaafo.com"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
          />
        </div>
        <div className="col-md-7 d-flex gap-2">
          <button
            className="admin-btn ghost"
            onClick={() => onFetch(false)}
            disabled={!domain?.trim() || loading}
            title="Fetch DNS records"
          >
            <RefreshCw size={16} /> {loading ? "Fetching…" : "Fetch"}
          </button>
        </div>
      </div>

      {/* Result meta */}
      {(summary?.message || resultInfo) && (
        <div className="border rounded p-2 mt-3 d-flex flex-wrap align-items-center gap-2 bg-light-subtle small">
          <div><b>{summary?.message || "Result"}</b></div>
          {resultInfo && (
            <>
              <span className="badge text-bg-light">page: {resultInfo.page}</span>
              <span className="badge text-bg-light">per_page: {resultInfo.per_page}</span>
              <span className="badge text-bg-light">count: {resultInfo.count}</span>
              <span className="badge text-bg-light">total: {resultInfo.total_count}</span>
            </>
          )}
        </div>
      )}

      {/* Create */}
      <div className="border rounded p-2 mt-3">
        <div className="row g-2">
          <div className="col-md-3">
            <label className="admin-field-label">Subdomain</label>
            <input
              className="form-control form-control-sm"
              placeholder="e.g., app"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </div>
        <div className="col-md-7">
  <label className="admin-field-label">Content (IP)</label>
  <select
    className={`form-select form-select-sm ${newContent ? (isIpLike ? "is-valid" : "is-invalid") : ""}`}
    value={newContent}
    onChange={(e) => setNewContent(e.target.value)}
  >
    {/* placeholder */}
    <option value="" disabled>Select an IP…</option>
    {(ipOptions.length ? ipOptions : ["35.200.161.138"]).map((ip) => (
      <option key={ip} value={ip}>{ip}</option>
    ))}
  </select>
  {newContent && !isIpLike && (
    <div className="invalid-feedback d-block" style={{ fontSize: 12 }}>
      Must be a valid IPv4 (e.g., 35.211.141.136).
    </div>
  )}
</div>

          <div className="col-md-2 d-flex align-items-end">
            <button
              className="admin-btn primary w-100"
              onClick={onCreate}
              disabled={creating || !isIpLike}
              title="Create subdomain"
            >
              <Plus size={16} />
              {creating ? "Creating…" : "Create"}
            </button>
          </div>
        </div>
      </div>

      {/* List (compact) */}
      <div className="mt-3">
        <div className="table-responsive">
          <table className="table table-sm table-compact align-middle">
            <thead className="text-center table-head-compact">
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>T.</th>
                <th>Content</th>
                <th>Pxbl</th>
                <th>Pxed</th>
                <th>TTL</th>
                <th>Created</th>
                <th>Modified</th>
                <th>A</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={row.id || `${row.name || "row"}-${idx}`}>
                  <td className="mono">{row.id || "-"}</td>
                  <td className="mono">{row.name || "-"}</td>
                  <td className="text-center">{row.type || "-"}</td>
                  <td className="mono truncate">{row.content ?? "-"}</td>
                  <td className="text-center">{String(row.proxiable ?? "-")}</td>
                  <td className="text-center">{String(row.proxied ?? "-")}</td>
                  <td className="text-center">{row.ttl ?? "-"}</td>
                  <td className="mono">{row.created_on ?? "-"}</td>
                  <td className="mono">{row.modified_on ?? "-"}</td>
                  <td className="text-center">
                    {/* ICON-ONLY buttons */}
                    <button
                      className="icon-btn"
                      title="View details"
                      aria-label="View details"
                      onClick={() => { setSelectedRow(row); setShowDetails(true); }}
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      className="icon-btn danger"
                      title="Delete"
                      aria-label="Delete"
                      onClick={() => onDelete?.(row)}
                      disabled={!!busyById?.[row.id]}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan="10" className="text-center text-muted py-3">
                    {loading ? "Loading…" : "No records found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Details Modal */}
      <div
        className={`modal fade ${showDetails ? "show d-block" : ""}`}
        tabIndex="-1"
        aria-hidden={!showDetails}
        style={{ background: showDetails ? "rgba(0,0,0,.4)" : "transparent" }}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header py-2">
              <h1 className="modal-title fs-6">
                Record — <span className="text-primary">{selectedRow?.name || "-"}</span>
              </h1>
              <button type="button" className="btn-close" onClick={() => setShowDetails(false)} />
            </div>
            <div className="modal-body py-3">
              {!selectedRow ? (
                <div className="text-center py-4 text-muted">No data</div>
              ) : (
                <div className="row g-2 small">
                  <div className="col-md-6">
                    <div className="border rounded p-2 h-100">
                      <div><b>ID</b>: <span className="mono">{selectedRow.id || "-"}</span></div>
                      <div><b>Name</b>: <span className="mono">{selectedRow.name || "-"}</span></div>
                      <div><b>Type</b>: {selectedRow.type || "-"}</div>
                      <div><b>TTL</b>: {selectedRow.ttl ?? "-"}</div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="border rounded p-2 h-100">
                      <div><b>Proxiable</b>: {String(selectedRow.proxiable ?? "-")}</div>
                      <div><b>Proxied</b>: {String(selectedRow.proxied ?? "-")}</div>
                      <div><b>Content</b>:</div>
                      <div className="mono" style={{ wordBreak: "break-word" }}>{selectedRow.content ?? "-"}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer py-2">
              <button className="admin-btn ghost" onClick={() => setShowDetails(false)}>Close</button>
            </div>
          </div>
        </div>
      </div>

      {/* Compact table + icon buttons styles */}
      <style>{`
        .table-compact thead tr th,
        .table-compact tbody tr td{
          padding: 6px 8px !important;
          font-size: 12px !important;
          vertical-align: middle;
          white-space: nowrap;
        }
        .table-head-compact{
          background-color:#01C37C !important;
          color:#0b2c3a;
        }
        .mono{ font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace; }
        .truncate{ max-width: 260px; overflow: hidden; text-overflow: ellipsis; }
        .icon-btn{
          height: 28px; width: 28px;
          display:inline-flex; align-items:center; justify-content:center;
          border:1px solid #d1d5db; border-radius:6px; background:#fff; margin:0 2px;
          transition: all .15s ease;
        }
        .icon-btn:hover{ filter:brightness(1.05); transform: translateY(-1px); }
        .icon-btn.danger{ border-color:#f3c2bf; background:#fff0f0; }
        .admin-field-label { font-size: .78rem; font-weight: 600; color: #374151; }
        .admin-btn{
          --bg:#f9fafb; --border:#d1d5db; --text:#111827;
          height:32px; padding:0 10px; font-size:.78rem; font-weight:600;
          border-radius:6px; border:1px solid var(--border);
          background:var(--bg); color:var(--text);
          display:inline-flex; align-items:center; gap:6px;
          transition:all .2s; cursor:pointer; white-space:nowrap;
        }
        .admin-btn.primary{ --bg:#01C37D; --border:#00a46a; --text:#fff; }
        .admin-btn.ghost{ --bg:#fff; --border:#d1d5db; --text:#111827; }
        .bg-light-subtle { background:#f8fafc; }
      `}</style>
    </div>
  );
};

export default Domain;
